import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Clock, Loader2, Plus, AlertTriangle, Calendar, Settings,
  Users, Trash2, Edit, Search, XCircle, CheckCircle, Eye
} from "lucide-react";

const TIPOS = [
  { value: "tardanza", label: "Tardanza", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" },
  { value: "ausencia_justificada", label: "Ausencia Justificada", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" },
  { value: "ausencia_injustificada", label: "Ausencia Injustificada", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" }
];

export default function TardanzasPage() {
  const [registros, setRegistros] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [resumen, setResumen] = useState({ resumen_por_empleado: [], totales: {} });
  const [config, setConfig] = useState({
    hora_entrada_default: "08:00",
    minutos_tolerancia: 10,
    max_tardanzas_mes: 3,
    max_ausencias_injustificadas_mes: 2,
    descuento_por_tardanza: false,
    descuento_por_minuto: 0,
    acumulacion_tardanzas: true,
    tardanzas_para_ausencia: 3,
    notificar_supervisor: true,
    notificar_rrhh: true,
    generar_alerta: true,
    activo: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("registros");
  const [showDialog, setShowDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedRegistro, setSelectedRegistro] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTipo, setFilterTipo] = useState("all");
  
  const [formData, setFormData] = useState({
    tipo: "tardanza",
    empleado_id: "",
    fecha: new Date().toISOString().split('T')[0],
    hora_entrada: "",
    hora_esperada: "08:00",
    minutos_tarde: 0,
    motivo: "",
    justificacion: ""
  });
  
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador", "gestion_humana"].includes(user?.role?.toLowerCase());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [registrosRes, empleadosRes, configRes, resumenRes] = await Promise.all([
        axios.get(`${API}/tardanzas?limit=200`, getAuthHeaders()),
        axios.get(`${API}/users`, getAuthHeaders()),
        axios.get(`${API}/tardanzas/configuracion`, getAuthHeaders()),
        axios.get(`${API}/tardanzas/resumen`, getAuthHeaders())
      ]);
      setRegistros(registrosRes.data);
      // Filter active users as employees
      const activeUsers = (empleadosRes.data || []).filter(u => u.is_active !== false);
      setEmpleados(activeUsers);
      setConfig(configRes.data);
      setResumen(resumenRes.data);
    } catch (err) {
      console.error("Error loading tardanzas data:", err);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/tardanzas/configuracion`, config, getAuthHeaders());
      toast.success("Configuración guardada");
    } catch (err) {
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.empleado_id || !formData.fecha) {
      toast.error("Complete los campos requeridos");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/tardanzas`, formData, getAuthHeaders());
      toast.success("Registro creado");
      setShowDialog(false);
      setFormData({
        tipo: "tardanza",
        empleado_id: "",
        fecha: new Date().toISOString().split('T')[0],
        hora_entrada: "",
        hora_esperada: "08:00",
        minutos_tarde: 0,
        motivo: "",
        justificacion: ""
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear registro");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Eliminar este registro?")) return;
    try {
      await axios.delete(`${API}/tardanzas/${id}`, getAuthHeaders());
      toast.success("Registro eliminado");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const calculateMinutesLate = (entrada, esperada) => {
    if (!entrada || !esperada) return 0;
    const [hE, mE] = entrada.split(':').map(Number);
    const [hEsp, mEsp] = esperada.split(':').map(Number);
    const totalEntrada = hE * 60 + mE;
    const totalEsperada = hEsp * 60 + mEsp;
    return Math.max(0, totalEntrada - totalEsperada);
  };

  const filteredRegistros = registros.filter(reg => {
    const matchesTipo = filterTipo === "all" || reg.tipo === filterTipo;
    const matchesSearch = !searchQuery || 
      reg.empleado_nombre?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTipo && matchesSearch;
  });

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="ml-0 md:ml-64 flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
                <Clock className="w-8 h-8 text-orange-500" />
                Tardanzas y Ausencias
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Control de asistencia y puntualidad
              </p>
            </div>
            {isAdmin && (
              <Button onClick={() => setShowDialog(true)} className="gap-2 bg-orange-600 hover:bg-orange-700" data-testid="new-tardanza-btn">
                <Plus className="w-4 h-4" />
                Nuevo Registro
              </Button>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">
                      {resumen.totales?.total_tardanzas || 0}
                    </p>
                    <p className="text-sm text-slate-500">Tardanzas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">
                      {resumen.totales?.total_ausencias_justificadas || 0}
                    </p>
                    <p className="text-sm text-slate-500">Justificadas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                    <XCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">
                      {resumen.totales?.total_ausencias_injustificadas || 0}
                    </p>
                    <p className="text-sm text-slate-500">Injustificadas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">
                      {resumen.totales?.empleados_con_registros || 0}
                    </p>
                    <p className="text-sm text-slate-500">Empleados</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-white dark:bg-slate-800 border dark:border-slate-700 mb-4">
              <TabsTrigger value="registros">Registros</TabsTrigger>
              <TabsTrigger value="resumen">Resumen por Empleado</TabsTrigger>
              {isAdmin && <TabsTrigger value="configuracion">Configuración</TabsTrigger>}
            </TabsList>

            <TabsContent value="registros">
              {/* Filters */}
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por empleado..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 dark:bg-slate-800 dark:border-slate-700"
                  />
                </div>
                <Select value={filterTipo} onValueChange={setFilterTipo}>
                  <SelectTrigger className="w-48 dark:bg-slate-800 dark:border-slate-700">
                    <SelectValue placeholder="Filtrar por tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {TIPOS.map(tipo => (
                      <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Table */}
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="p-0">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                    </div>
                  ) : filteredRegistros.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">No hay registros</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 dark:bg-slate-900">
                          <tr>
                            <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Empleado</th>
                            <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Tipo</th>
                            <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Fecha</th>
                            <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Hora</th>
                            <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Min. Tarde</th>
                            <th className="text-right p-4 font-medium text-slate-600 dark:text-slate-300">Acciones</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y dark:divide-slate-700">
                          {filteredRegistros.map(reg => {
                            const tipo = TIPOS.find(t => t.value === reg.tipo);
                            return (
                              <tr key={reg.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                                <td className="p-4">
                                  <div>
                                    <p className="font-medium text-slate-900 dark:text-white">{reg.empleado_nombre}</p>
                                    <p className="text-sm text-slate-500">{reg.departamento_nombre}</p>
                                  </div>
                                </td>
                                <td className="p-4">
                                  <Badge className={tipo?.color}>{tipo?.label}</Badge>
                                </td>
                                <td className="p-4 text-slate-600 dark:text-slate-300">{reg.fecha}</td>
                                <td className="p-4 text-slate-600 dark:text-slate-300">
                                  {reg.hora_entrada || "-"}
                                </td>
                                <td className="p-4">
                                  {reg.minutos_tarde > 0 && (
                                    <Badge variant="outline" className="text-amber-600 border-amber-300">
                                      {reg.minutos_tarde} min
                                    </Badge>
                                  )}
                                </td>
                                <td className="p-4">
                                  <div className="flex items-center justify-end gap-2">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => { setSelectedRegistro(reg); setShowViewDialog(true); }}
                                    >
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                    {isAdmin && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-red-600"
                                        onClick={() => handleDelete(reg.id)}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="resumen">
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader>
                  <CardTitle>Resumen del Mes - {resumen.periodo?.month}/{resumen.periodo?.year}</CardTitle>
                </CardHeader>
                <CardContent>
                  {resumen.resumen_por_empleado?.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No hay datos para este período</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 dark:bg-slate-900">
                          <tr>
                            <th className="text-left p-3 font-medium text-slate-600 dark:text-slate-300">Empleado</th>
                            <th className="text-center p-3 font-medium text-slate-600 dark:text-slate-300">Tardanzas</th>
                            <th className="text-center p-3 font-medium text-slate-600 dark:text-slate-300">Aus. Justificadas</th>
                            <th className="text-center p-3 font-medium text-slate-600 dark:text-slate-300">Aus. Injustificadas</th>
                            <th className="text-center p-3 font-medium text-slate-600 dark:text-slate-300">Min. Tarde Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y dark:divide-slate-700">
                          {resumen.resumen_por_empleado?.map(emp => (
                            <tr key={emp._id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                              <td className="p-3 font-medium text-slate-900 dark:text-white">{emp.empleado_nombre}</td>
                              <td className="p-3 text-center">
                                <Badge className={emp.total_tardanzas > config.max_tardanzas_mes ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}>
                                  {emp.total_tardanzas}
                                </Badge>
                              </td>
                              <td className="p-3 text-center">
                                <Badge className="bg-blue-100 text-blue-700">{emp.total_ausencias_justificadas}</Badge>
                              </td>
                              <td className="p-3 text-center">
                                <Badge className={emp.total_ausencias_injustificadas > config.max_ausencias_injustificadas_mes ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}>
                                  {emp.total_ausencias_injustificadas}
                                </Badge>
                              </td>
                              <td className="p-3 text-center text-slate-600 dark:text-slate-300">
                                {emp.minutos_tarde_total} min
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {isAdmin && (
              <TabsContent value="configuracion">
                <Card className="dark:bg-slate-800 dark:border-slate-700">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Settings className="w-5 h-5" />
                      Configuración de Tardanzas y Ausencias
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Hora de Entrada Default</Label>
                        <Input
                          type="time"
                          value={config.hora_entrada_default}
                          onChange={(e) => setConfig({...config, hora_entrada_default: e.target.value})}
                          className="dark:bg-slate-700 dark:border-slate-600"
                        />
                      </div>
                      <div>
                        <Label>Minutos de Tolerancia</Label>
                        <Input
                          type="number"
                          min="0"
                          value={config.minutos_tolerancia}
                          onChange={(e) => setConfig({...config, minutos_tolerancia: parseInt(e.target.value)})}
                          className="dark:bg-slate-700 dark:border-slate-600"
                        />
                      </div>
                      <div>
                        <Label>Máx. Tardanzas por Mes</Label>
                        <Input
                          type="number"
                          min="1"
                          value={config.max_tardanzas_mes}
                          onChange={(e) => setConfig({...config, max_tardanzas_mes: parseInt(e.target.value)})}
                          className="dark:bg-slate-700 dark:border-slate-600"
                        />
                      </div>
                      <div>
                        <Label>Máx. Ausencias Injustificadas por Mes</Label>
                        <Input
                          type="number"
                          min="1"
                          value={config.max_ausencias_injustificadas_mes}
                          onChange={(e) => setConfig({...config, max_ausencias_injustificadas_mes: parseInt(e.target.value)})}
                          className="dark:bg-slate-700 dark:border-slate-600"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base">Descuento por Tardanza</Label>
                        <p className="text-sm text-slate-500">Aplicar descuento salarial</p>
                      </div>
                      <Switch
                        checked={config.descuento_por_tardanza}
                        onCheckedChange={(v) => setConfig({...config, descuento_por_tardanza: v})}
                      />
                    </div>

                    {config.descuento_por_tardanza && (
                      <div>
                        <Label>Descuento por Minuto (RD$)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={config.descuento_por_minuto}
                          onChange={(e) => setConfig({...config, descuento_por_minuto: parseFloat(e.target.value)})}
                          className="dark:bg-slate-700 dark:border-slate-600"
                        />
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base">Acumulación de Tardanzas</Label>
                        <p className="text-sm text-slate-500">Convertir tardanzas acumuladas en ausencia</p>
                      </div>
                      <Switch
                        checked={config.acumulacion_tardanzas}
                        onCheckedChange={(v) => setConfig({...config, acumulacion_tardanzas: v})}
                      />
                    </div>

                    {config.acumulacion_tardanzas && (
                      <div>
                        <Label>Tardanzas para 1 Ausencia</Label>
                        <Input
                          type="number"
                          min="1"
                          value={config.tardanzas_para_ausencia}
                          onChange={(e) => setConfig({...config, tardanzas_para_ausencia: parseInt(e.target.value)})}
                          className="dark:bg-slate-700 dark:border-slate-600"
                        />
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base">Notificar al Supervisor</Label>
                      </div>
                      <Switch
                        checked={config.notificar_supervisor}
                        onCheckedChange={(v) => setConfig({...config, notificar_supervisor: v})}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base">Notificar a RRHH</Label>
                      </div>
                      <Switch
                        checked={config.notificar_rrhh}
                        onCheckedChange={(v) => setConfig({...config, notificar_rrhh: v})}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base">Generar Alertas</Label>
                        <p className="text-sm text-slate-500">Alertar cuando se excedan límites</p>
                      </div>
                      <Switch
                        checked={config.generar_alerta}
                        onCheckedChange={(v) => setConfig({...config, generar_alerta: v})}
                      />
                    </div>

                    <Button onClick={handleSaveConfig} disabled={saving} className="w-full bg-orange-600 hover:bg-orange-700">
                      {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Guardar Configuración
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>
        </div>

        {/* New Record Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>Registrar Tardanza/Ausencia</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Tipo *</Label>
                <Select value={formData.tipo} onValueChange={(v) => setFormData({...formData, tipo: v})}>
                  <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS.map(tipo => (
                      <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Empleado *</Label>
                <Select value={formData.empleado_id} onValueChange={(v) => setFormData({...formData, empleado_id: v})}>
                  <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                    <SelectValue placeholder="Seleccionar empleado" />
                  </SelectTrigger>
                  <SelectContent>
                    {empleados.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name || emp.nombre_completo || emp.nombre || emp.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Fecha *</Label>
                <Input
                  type="date"
                  value={formData.fecha}
                  onChange={(e) => setFormData({...formData, fecha: e.target.value})}
                  className="dark:bg-slate-700 dark:border-slate-600"
                />
              </div>

              {formData.tipo === "tardanza" && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Hora Esperada</Label>
                      <Input
                        type="time"
                        value={formData.hora_esperada}
                        onChange={(e) => {
                          const esperada = e.target.value;
                          setFormData({
                            ...formData,
                            hora_esperada: esperada,
                            minutos_tarde: calculateMinutesLate(formData.hora_entrada, esperada)
                          });
                        }}
                        className="dark:bg-slate-700 dark:border-slate-600"
                      />
                    </div>
                    <div>
                      <Label>Hora de Entrada</Label>
                      <Input
                        type="time"
                        value={formData.hora_entrada}
                        onChange={(e) => {
                          const entrada = e.target.value;
                          setFormData({
                            ...formData,
                            hora_entrada: entrada,
                            minutos_tarde: calculateMinutesLate(entrada, formData.hora_esperada)
                          });
                        }}
                        className="dark:bg-slate-700 dark:border-slate-600"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Minutos de Tardanza: <span className="font-bold text-amber-600">{formData.minutos_tarde}</span></Label>
                  </div>
                </>
              )}

              <div>
                <Label>Motivo</Label>
                <Textarea
                  value={formData.motivo}
                  onChange={(e) => setFormData({...formData, motivo: e.target.value})}
                  placeholder="Describa el motivo..."
                  className="dark:bg-slate-700 dark:border-slate-600"
                />
              </div>

              {formData.tipo !== "ausencia_injustificada" && (
                <div>
                  <Label>Justificación</Label>
                  <Textarea
                    value={formData.justificacion}
                    onChange={(e) => setFormData({...formData, justificacion: e.target.value})}
                    placeholder="Justificación o documentación..."
                    className="dark:bg-slate-700 dark:border-slate-600"
                  />
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
                <Button type="submit" disabled={saving} className="bg-orange-600 hover:bg-orange-700">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Registrar
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>Detalle del Registro</DialogTitle>
            </DialogHeader>
            {selectedRegistro && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Empleado</p>
                    <p className="font-medium dark:text-white">{selectedRegistro.empleado_nombre}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Departamento</p>
                    <p className="font-medium dark:text-white">{selectedRegistro.departamento_nombre || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Tipo</p>
                    <Badge className={TIPOS.find(t => t.value === selectedRegistro.tipo)?.color}>
                      {TIPOS.find(t => t.value === selectedRegistro.tipo)?.label}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Fecha</p>
                    <p className="font-medium dark:text-white">{selectedRegistro.fecha}</p>
                  </div>
                  {selectedRegistro.tipo === "tardanza" && (
                    <>
                      <div>
                        <p className="text-sm text-slate-500">Hora Entrada</p>
                        <p className="font-medium dark:text-white">{selectedRegistro.hora_entrada || "-"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Minutos Tarde</p>
                        <p className="font-medium text-amber-600">{selectedRegistro.minutos_tarde || 0} min</p>
                      </div>
                    </>
                  )}
                </div>
                {selectedRegistro.motivo && (
                  <div>
                    <p className="text-sm text-slate-500">Motivo</p>
                    <p className="dark:text-white">{selectedRegistro.motivo}</p>
                  </div>
                )}
                {selectedRegistro.justificacion && (
                  <div>
                    <p className="text-sm text-slate-500">Justificación</p>
                    <p className="dark:text-white">{selectedRegistro.justificacion}</p>
                  </div>
                )}
                <div className="pt-2 border-t dark:border-slate-700 text-sm text-slate-500">
                  Registrado por: {selectedRegistro.registrado_por_nombre} el {new Date(selectedRegistro.created_at).toLocaleString()}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
